/**
 * =============================================================================
 * SCRIPT-NAME:   uje_UserJourneyExplorer.js
 * MODUL:         User-Journey & Funnel Explorer (UJE)
 * BESCHREIBUNG:  Frontend-Controller.
 * FEATURE v3.1:  Anzeige von Nachname und berechneter Gesamtverweildauer im Pfad.
 *
 * VERSION:       v3.1
 * DATUM:         2026-02-13
 * AUTHOR:        KI-Core
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./uje_UserJourneyExplorer.css";

export default function UserJourneyExplorer() {
    // --- ZEITRAUM STATES ---
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    
    // --- FILTER STATES ---
    const [campaignList, setCampaignList] = useState([]);
    const [termList, setTermList] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState("");
    const [selectedTerm, setSelectedTerm] = useState(""); 

    // --- DATA STATES ---
    const [visitors, setVisitors] = useState([]);
    const [selectedPath, setSelectedPath] = useState(null);
    const [sectionStats, setSectionStats] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const token = localStorage.getItem("token");

    // 1. KASKADE: Kampagne -> Keywords
    useEffect(() => {
        if (selectedCampaign) {
            fetchFilters('terms', selectedCampaign);
            setSelectedTerm(""); 
        } else {
            setTermList([]);
            setSelectedTerm("");
        }
    }, [selectedCampaign]);

    // Hilfsfunktion: Filter laden
    const fetchFilters = async (mode, campaignParam = '') => {
        try {
            const url = `https://freiseinapp.de/api/uje_get_filters.php?mode=${mode}&campaign=${encodeURIComponent(campaignParam)}&startDate=${startDate}&endDate=${endDate}`;
            const res = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            const json = await res.json();
            if (json.success) {
                if (mode === 'campaigns') setCampaignList(json.data || []);
                if (mode === 'terms') setTermList(json.data || []);
            }
        } catch (e) { console.error("Filter-Ladefehler:", e); }
    };

    // HAUPTFUNKTION (Der Button-Klick)
    const loadAllData = async () => {
        setLoading(true);
        setSelectedPath(null); 
        try {
            const queryParams = `startDate=${startDate}&endDate=${endDate}&campaign=${encodeURIComponent(selectedCampaign)}&term=${encodeURIComponent(selectedTerm)}`;

            // 1. Besucherliste
            const listRes = await fetch(`https://freiseinapp.de/api/uje_get_visitors.php?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const listData = await listRes.json();
            if (listData.success) setVisitors(listData.visitorListData || []);

            // 2. Statistik
            const statsRes = await fetch(`https://freiseinapp.de/api/uje_get_stats.php?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });
            const statsData = await statsRes.json();
            if (statsData.success) setSectionStats(statsData.stats || []);

            // 3. Filter aktualisieren
            await fetchFilters('campaigns');

        } catch (e) { console.error("Haupt-Ladefehler:", e); }
        setLoading(false);
    };

    // Einzelnen Pfad laden
    const loadPath = async (visitorId) => {
        setLoading(true);
        try {
            const url = `https://freiseinapp.de/api/uje_get_path.php?visitorId=${encodeURIComponent(visitorId)}&startDate=${startDate}&endDate=${endDate}`;
            const response = await fetch(url, { headers: { "Authorization": `Bearer ${token}` } });
            const data = await response.json();
            if (data.success) setSelectedPath(data);
        } catch (e) { console.error("Pfad-Ladefehler:", e); }
        setLoading(false);
    };

    const renderStatusIcons = (milestones) => {
        if (!milestones) return <span style={{ opacity: 0.2 }}>---</span>;
        return (
            <div className="status-icon-wrapper" style={{ display: 'flex', gap: '8px', fontSize: '1.2rem' }}>
                <span title="Video Gestartet" style={{ opacity: milestones.webinar_booked ? 1 : 0.15 }}>📽️</span>
                <span title="Video Fertig" style={{ opacity: milestones.webinar_engaged ? 1 : 0.15 }}>✅</span>
                <span title="Kontakt/Klick" style={{ opacity: milestones.eg_booked ? 1 : 0.15 }}>📞</span>
            </div>
        );
    };

    // Berechnung der Verweildauer für den individuellen Pfad
    let totalDuration = "0m 0s";
    if (selectedPath && selectedPath.path && selectedPath.path.length > 0) {
        // ' ' durch 'T' ersetzen für sauberes Parsing in allen Browsern (Safari Kompatibilität)
        const firstEvent = new Date(selectedPath.path[0].event_timestamp.replace(' ', 'T'));
        const lastEvent = new Date(selectedPath.path[selectedPath.path.length - 1].event_timestamp.replace(' ', 'T'));
        const diffMs = lastEvent - firstEvent;
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor((diffMs % 60000) / 1000);
        totalDuration = `${diffMins}m ${diffSecs}s`;
    }

    return (
        <div className="analytics-page-wrapper">
            <div className="analytics-dashboard-container">
                <header className="analytics-header">
                    <h2>👣 User-Journey & Funnel Explorer</h2>
                </header>

                <div className="date-filter-section" style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div className="filter-group">
                        <label>Von</label>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="date-input" />
                    </div>
                    <div className="filter-group">
                        <label>Bis</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="date-input" />
                    </div>
                    
                    <button onClick={loadAllData} className="analyze-button" disabled={loading}>
                        {loading ? "Lade..." : "🔍 Daten analysieren"}
                    </button>

                    <div className="filter-group" style={{ minWidth: '220px' }}>
                        <label>Kampagne</label>
                        <select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)} className="funnel-lp-select">
                            <option value="">-- Alle --</option>
                            {campaignList.map((cam, idx) => <option key={idx} value={cam}>{cam}</option>)}
                        </select>
                    </div>

                    <div className="filter-group" style={{ minWidth: '220px' }}>
                        <label>Keyword</label>
                        <select value={selectedTerm} onChange={(e) => setSelectedTerm(e.target.value)} className="funnel-lp-select" disabled={!selectedCampaign} style={{ opacity: selectedCampaign ? 1 : 0.5 }}>
                            <option value="">-- Alle --</option>
                            {termList.map((t, idx) => <option key={idx} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                <div className="explorer-grid">
                    {/* Linke Seite: Besucher */}
                    <div className="results-section">
                        <h3>Besucher ({visitors.length})</h3>
                        <div className="table-wrapper">
                            <table className="analytics-table">
                                <thead><tr><th>Identität / Zeit</th><th>Status</th><th>Dauer</th></tr></thead>
                                <tbody>
                                    {visitors.map(v => (
                                        <tr key={v.visitor_id} onClick={() => loadPath(v.visitor_id)} className={`clickable-row ${selectedPath?.visitorId === v.visitor_id ? 'active-row' : ''}`}>
                                            <td>
                                                <div className="visitor-info" style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '1.5rem', marginRight: '10px' }} title="Gerätetyp">
                                                        {v.device_icon || '❓'}
                                                    </span>
                                                    <div>
                                                        <strong>{v.vorname ? `${v.vorname} ${v.nachname || ''}` : "Anonym"}</strong>
                                                        <br/>
                                                        <small>{v.first_seen?.split(' ')[1].substring(0,5)} Uhr</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{renderStatusIcons(v.milestones)}</td>
                                            <td><small>{v.session_duration_formatted}</small></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Rechte Seite: Pfad */}
                    <div className="results-section">
                        <h3>Individueller Pfad</h3>
                        {selectedPath ? (
                            <div className="timeline">
                                <div className="identity-header-box" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '10px', marginBottom: '20px', borderLeft: '6px solid #28a745' }}>
                                    
                                    {/* 1. NAME, GESAMTZEIT & KEYWORD */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <div>
                                            <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>USER</small><br/>
                                            <strong>{selectedPath.meta.vorname || "Unbekannt"} {selectedPath.meta.nachname || ""}</strong>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>VERWEILDAUER</small><br/>
                                            <span style={{ color: '#17a2b8', fontWeight: 'bold' }}>{totalDuration}</span>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>KEYWORD</small><br/>
                                            <span style={{ color: '#d63384', fontWeight: 'bold' }}>{selectedPath.meta.utm_term || '-'}</span>
                                        </div>
                                    </div>

                                    {/* 2. USER AGENT (Technische Details) */}
                                    <div style={{ marginBottom: '10px', borderTop:'1px solid #e9ecef', paddingTop:'5px' }}>
                                        <small style={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>SYSTEM-INFO (User Agent)</small><br/>
                                        <div style={{ 
                                            fontSize: '0.75rem', 
                                            color: '#555', 
                                            fontFamily: 'Consolas, monospace', 
                                            wordBreak: 'break-all', 
                                            backgroundColor: '#fff',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            border: '1px solid #e9ecef'
                                        }}>
                                            {selectedPath.meta.user_agent || "Keine Info verfügbar"}
                                        </div>
                                    </div>

                                    {/* 3. VISITOR ID */}
                                    <div style={{ fontSize: '0.7rem', color: '#adb5bd', fontFamily: 'monospace', textAlign: 'right' }}>
                                        ID: {selectedPath.visitorId}
                                    </div>
                                </div>

                                <ul className="path-list">
                                    {selectedPath.path.map((step, i) => {
                                        let detailText = "Page Load";
                                        let icon = "⚪";

                                        if (step.event_type === 'SectionView') {
                                            detailText = `Sektion: ${step.section_name}`;
                                            icon = "👁️";
                                        } else if (step.event_type === 'CtaClick') {
                                            detailText = `Klick: ${step.cta_text}`;
                                            icon = "🖱️";
                                        } else if (step.event_type.startsWith('Video')) {
                                            const title = step.video_title || "Video";
                                            if (step.event_type === 'VideoStart') { detailText = `Start: ${title}`; icon = "▶️"; }
                                            else if (step.event_type === 'VideoComplete') { detailText = `Fertig: ${title}`; icon = "✅"; }
                                            else if (step.event_type === 'VideoProgress') { detailText = `${title} (${step.video_progress}%)`; icon = "📺"; }
                                        } else if (step.event_type === 'PageLeaveEnd') {
                                            detailText = "Seite verlassen";
                                            icon = "🚪";
                                        }

                                        return (
                                            <li key={i} className={`path-item event-${step.event_type}`}>
                                                <span className="path-time">{step.event_timestamp?.split(' ')[1].substring(0, 8)}</span>
                                                <span className="path-icon" style={{marginRight:'10px', fontSize:'1.2rem'}}>{icon}</span>
                                                <span className="path-event-type">{step.event_type}</span>
                                                <span className="path-detail">{detailText}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : <div className="placeholder-text">Wähle einen Besucher.</div>}
                    </div>
                </div>

                <div className="stats-summary-section">
                    <h3>📊 Sektions-Performance (Global)</h3>
                    {sectionStats.length > 0 ? (
                        <div className="table-wrapper">
                            <table className="analytics-table stats-table">
                                <thead><tr><th>Sektion</th><th>Views</th><th>Ø Dauer</th><th>Abbruch</th><th>Status</th></tr></thead>
                                <tbody>
                                    {sectionStats.map((stat, index) => (
                                        <tr key={index}>
                                            <td><strong>{stat.section}</strong></td>
                                            <td>{stat.total_views}</td>
                                            <td>{stat.avg_duration_sec}s</td>
                                            <td style={{ color: stat.drop_off_rate > 35 ? '#dc3545' : '#28a745' }}>{stat.drop_off_rate}%</td>
                                            <td>{stat.drop_off_rate > 35 ? '⚠️ Flaschenhals' : '✅ OK'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : <div className="placeholder-text">Drücke auf "Analysieren" um Daten zu laden.</div>}
                </div>
                <BackButton to="/admin" />
            </div>
        </div>
    );
}